/**
 * Get Active Panel Utility
 * 
 * Extracts the currently active panel from the FlexLayout model.
 * Maps FlexLayout component names to panel identifiers for permalinks.
 */

/**
 * Maps FlexLayout component names to panel identifiers
 * Only panels that should appear in URLs are mapped here
 */
const COMPONENT_TO_PANEL_MAP = {
  'info': 'info',
  'notes': 'notes',
  'editorWrapper': 'editor',
  'openNotesPanel': 'open-notes',
  // Backwards compatibility: saved FlexLayout models may still use the old component name
  'noteTabsMobile': 'open-notes',
  // 'notebooks': 'notebooks', // Don't include default navigation panels in URL
  // 'aofCollections': 'aof', // Don't include default navigation panels in URL
};

/**
 * Normalizes a FlexLayout model into its JSON representation.
 * Handles both FlexLayout Model instances and plain JSON objects.
 */
const normalizeFlexLayoutModel = (flexLayoutModel) => {
  if (!flexLayoutModel) {
    return null;
  }

  let modelData = flexLayoutModel;

  // If it's a FlexLayout Model instance, get the JSON
  if (flexLayoutModel.toJson && typeof flexLayoutModel.toJson === 'function') {
    try {
      modelData = flexLayoutModel.toJson();
    } catch (e) {
      // If toJson fails, try _json property
      modelData = flexLayoutModel._json || flexLayoutModel;
    }
  } else if (flexLayoutModel._json) {
    // Some FlexLayout versions store JSON in _json property
    modelData = flexLayoutModel._json;
  }

  return modelData;
};

/**
 * Helper function to find all visible/active components in a node
 */
const findAllActiveComponentsInNode = (node, activeComponents = []) => {
      if (!node) return activeComponents;

      // If this is a tabset, check its selected tab
      if (node.type === 'tabset' && node.children && Array.isArray(node.children)) {
        // The 'selected' property indicates the index of the selected tab
        const selectedIndex = node.selected;
        if (selectedIndex !== undefined && selectedIndex !== null && node.children[selectedIndex]) {
          const selectedTab = node.children[selectedIndex];
          if (selectedTab.component) {
            activeComponents.push(selectedTab.component);
          }
        }
        
        // For tabsets with only one tab, always include it (like editorWrapper or openNotesPanel in its own tabset)
        if (node.children.length === 1 && node.children[0].component) {
          const singleComponent = node.children[0].component;
          // Always include single-tab components when they exist
          if (!activeComponents.includes(singleComponent)) {
            activeComponents.push(singleComponent);
          }
        }
      }

      // Recursively search children
      if (node.children && Array.isArray(node.children)) {
        for (const child of node.children) {
          findAllActiveComponentsInNode(child, activeComponents);
        }
      }

  return activeComponents;
};

/**
 * Helper function to find all components that exist in the model (for notes, editor, info)
 */
const findAllComponentsInNode = (node, allComponents = []) => {
      if (!node) return allComponents;

      if (node.type === 'tab' && node.component) {
        allComponents.push(node.component);
      }

      if (node.children && Array.isArray(node.children)) {
        for (const child of node.children) {
          findAllComponentsInNode(child, allComponents);
        }
      }

  return allComponents;
};

/**
 * Helper to collect tabset (column) information for debugging and layout introspection.
 */
const collectTabsets = (node, path = [], tabsets = []) => {
  if (!node) return tabsets;

  if (node.type === 'tabset' && Array.isArray(node.children)) {
    tabsets.push({
      path,
      selected: node.selected ?? 0,
      tabs: node.children
        .filter((child) => child && child.type === 'tab')
        .map((child) => ({
          name: child.name,
          component: child.component,
        })),
    });
  }

  if (Array.isArray(node.children)) {
    node.children.forEach((child, index) => {
      collectTabsets(child, [...path, index], tabsets);
    });
  }

  return tabsets;
};

/**
 * Gets all active panels from a FlexLayout model
 * @param {Object} flexLayoutModel - FlexLayout model object (can be JSON or Model instance)
 * @returns {string[]} Array of panel identifiers (empty array if none found)
 */
export const getActivePanelFromFlexLayout = (flexLayoutModel) => {
  if (!flexLayoutModel) {
    return [];
  }

  try {
    const modelData = normalizeFlexLayoutModel(flexLayoutModel);

    // Start searching from the layout root
    // FlexLayout JSON structure: { layout: { type: 'row', children: [...] } }
    if (modelData && modelData.layout) {
      // Use tabsets (columns) so that each column contributes its selected panel
      const tabsets = collectTabsets(modelData.layout, [], []);

      const orderedPanels = tabsets
        .map((tabset) => {
          const { selected, tabs } = tabset;
          if (!tabs || tabs.length === 0) {
            return null;
          }
          const selectedIndex =
            selected !== undefined && selected !== null ? selected : 0;
          const selectedTab = tabs[selectedIndex] || tabs[0];
          if (!selectedTab || !selectedTab.component) {
            return null;
          }
          const panelId = COMPONENT_TO_PANEL_MAP[selectedTab.component];
          return panelId || null;
        })
        .filter((panelId) => panelId);

      // Remove duplicates while preserving column order
      const seen = new Set();
      const result = [];
      orderedPanels.forEach((panelId) => {
        if (!seen.has(panelId)) {
          seen.add(panelId);
          result.push(panelId);
        }
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('getActivePanel - orderedPanels:', orderedPanels);
        console.log('getActivePanel - final result:', result);
      }

      return result;
    }

    return [];
  } catch (error) {
    console.error('Error extracting active panels from FlexLayout model:', error, flexLayoutModel);
    return [];
  }
};

/**
 * Gets all active panels from app state context
 * @param {Object} appStateContext - XState app state context
 * @returns {string[]} Array of panel identifiers (empty array if none found)
 */
export const getActivePanel = (appStateContext) => {
  if (!appStateContext) {
    return [];
  }

  const { activeFlexLayoutModel } = appStateContext;
  return getActivePanelFromFlexLayout(activeFlexLayoutModel);
};

/**
 * Returns detailed layout information for debugging and ledger logging.
 * Includes all tabsets (columns), their tabs, and which tab is selected.
 * @param {Object} flexLayoutModel
 * @returns {{
 *   columns: Array<{
 *     index: number,
 *     selectedTabName: string | null,
 *     selectedComponent: string | null,
 *     tabs: Array<{ name: string, component: string }>
 *   }>,
 *   activeComponents: string[],
 *   allComponents: string[]
 * }}
 */
export const getPanelLayoutDebugInfo = (flexLayoutModel) => {
  const modelData = normalizeFlexLayoutModel(flexLayoutModel);

  if (!modelData || !modelData.layout) {
    return {
      columns: [],
      activeComponents: [],
      allComponents: [],
    };
  }

  const activeComponents = findAllActiveComponentsInNode(modelData.layout, []);
  const allComponents = findAllComponentsInNode(modelData.layout, []);
  const tabsets = collectTabsets(modelData.layout, [], []);

  const columns = tabsets.map((tabset, index) => {
    const { selected, tabs } = tabset;
    const selectedTab = tabs && tabs.length > 0 ? tabs[selected ?? 0] : null;

    return {
      index,
      selectedTabName: selectedTab ? selectedTab.name : null,
      selectedComponent: selectedTab ? selectedTab.component : null,
      tabs,
    };
  });

  return {
    columns,
    activeComponents,
    allComponents,
  };
};


